import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Play, Sparkles } from "lucide-react";
import { AE_LOGO_URL } from "@/lib/constants";
import Streaks from "@/components/Streaks";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const badgeY = useTransform(scrollYProgress, [0, 1], [0, 120]);
    const badgeScale = useTransform(scrollYProgress, [0, 1], [1, 0.85]);
    const badgeRot = useTransform(scrollYProgress, [0, 1], [0, -10]);

    return (
        <section
            ref={ref}
            id="top"
            data-testid="hero-section"
            className="relative pt-28 md:pt-36 pb-20 md:pb-32 overflow-hidden bg-black"
        >
            {/* Decorative background */}
            <div className="pointer-events-none absolute inset-0 ae-grid-bg-dark opacity-50 ae-mask-fade-b" />
            <div className="pointer-events-none absolute -top-32 -right-32 h-[640px] w-[640px] ae-radial-orange" />
            <Streaks />
            {/* Fade into light About section */}
            <div className="pointer-events-none absolute bottom-0 inset-x-0 h-32 md:h-48 bg-gradient-to-b from-transparent to-ae-gray-100 z-10" />

            <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    {/* Right – Copy */}
                    <div className="lg:col-span-7 lg:order-2">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            className="inline-flex items-center gap-2 rounded-full border border-ae-orange/30 bg-ae-orange/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-ae-orange"
                            data-testid="hero-badge-label"
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            AI-Enhanced Advertising Agency
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.9,
                                ease: [0.22, 1, 0.36, 1],
                                delay: 0.1,
                            }}
                            className="mt-6 font-display font-extrabold tracking-[-0.035em] text-white text-[3.25rem] sm:text-[4.5rem] md:text-[5.5rem] lg:text-[6.5rem] leading-[0.98]"
                            data-testid="hero-headline"
                        >
                            AE is
                            <br />
                            Advertising
                            <br />
                            <span className="relative inline-block">
                                <span className="relative z-10 text-ae-orange">
                                    Enhanced
                                </span>
                                <span
                                    aria-hidden
                                    className="absolute -bottom-2 left-0 right-0 h-3 md:h-4 bg-ae-orange/15 rounded-full -z-0"
                                />
                            </span>
                            <span className="text-ae-orange">.</span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.9,
                                ease: [0.22, 1, 0.36, 1],
                                delay: 0.2,
                            }}
                            className="mt-6 max-w-xl text-lg md:text-xl text-white/75 leading-relaxed"
                            data-testid="hero-subhead"
                        >
                            AI-powered advertising, software and digitally enhanced brand
                            experiences — built to help bold brands turn ideas into
                            measurable digital and real-world impact.
                            <span
                                className="block mt-3 font-display font-bold text-ae-orange tracking-tight"
                                data-testid="hero-tagline"
                            >
                                Amplify Everything.
                            </span>
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.9,
                                ease: [0.22, 1, 0.36, 1],
                                delay: 0.3,
                            }}
                            className="mt-9 flex flex-wrap items-center gap-4"
                        >
                            <a
                                href="#contact"
                                data-testid="hero-cta-start-project"
                                className="group inline-flex items-center gap-2 bg-ae-orange text-white rounded-full px-7 py-4 text-sm md:text-base font-semibold hover:bg-ae-orange-light shadow-ae-lift hover:shadow-ae-glow hover:-translate-y-0.5 transition-all duration-300"
                            >
                                Start a Project
                                <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform" />
                            </a>
                            <a
                                href="#work"
                                data-testid="hero-cta-view-work"
                                className="group inline-flex items-center gap-2 bg-white text-ae-ink border border-white/20 rounded-full px-7 py-4 text-sm md:text-base font-semibold hover:bg-white/10 hover:text-white hover:border-white/30 transition-all duration-300"
                            >
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ae-orange/15 text-ae-orange group-hover:bg-white/15 group-hover:text-white transition-colors">
                                    <Play className="h-3 w-3 fill-current" />
                                </span>
                                View Our Work
                            </a>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1, delay: 0.55 }}
                            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs uppercase tracking-[0.22em] font-semibold text-white/60"
                            data-testid="hero-service-line"
                        >
                            <span>Strategy</span>
                            <span className="h-1 w-1 rounded-full bg-ae-orange" />
                            <span>Creative</span>
                            <span className="h-1 w-1 rounded-full bg-ae-orange" />
                            <span>AI</span>
                            <span className="h-1 w-1 rounded-full bg-ae-orange" />
                            <span>Advertising</span>
                            <span className="h-1 w-1 rounded-full bg-ae-orange" />
                            <span>Software</span>
                            <span className="h-1 w-1 rounded-full bg-ae-orange" />
                            <span>Interactive Activations</span>
                        </motion.div>
                    </div>

                    {/* Left – AE Glossy Badge */}
                    <div className="lg:col-span-5 relative lg:order-1">
                        <motion.div
                            style={{
                                y: badgeY,
                                scale: badgeScale,
                                rotate: badgeRot,
                            }}
                            className="relative mx-auto w-[280px] sm:w-[360px] md:w-[440px] lg:w-[520px] aspect-square"
                            data-testid="hero-badge-visual"
                        >
                            {/* Soft glow */}
                            <div className="absolute inset-6 rounded-full bg-ae-orange/30 blur-3xl" />
                            {/* Rotating ring */}
                            <div className="absolute inset-0 animate-glow-spin">
                                <svg
                                    viewBox="0 0 400 400"
                                    className="w-full h-full"
                                    aria-hidden
                                >
                                    <defs>
                                        <linearGradient
                                            id="ringG"
                                            x1="0"
                                            x2="1"
                                            y1="0"
                                            y2="1"
                                        >
                                            <stop offset="0" stopColor="#F36C21" />
                                            <stop
                                                offset="1"
                                                stopColor="#FF8A3D"
                                                stopOpacity="0"
                                            />
                                        </linearGradient>
                                    </defs>
                                    <circle
                                        cx="200"
                                        cy="200"
                                        r="180"
                                        fill="none"
                                        stroke="url(#ringG)"
                                        strokeWidth="1.5"
                                        strokeDasharray="3 10"
                                    />
                                </svg>
                            </div>

                            {/* The badge */}
                            <motion.img
                                src={AE_LOGO_URL}
                                alt="AE — Amplified eSolutions"
                                style={{
                                    WebkitMaskImage:
                                        "radial-gradient(circle at 50% 48%, black 38%, rgba(0,0,0,0.85) 46%, transparent 58%)",
                                    maskImage:
                                        "radial-gradient(circle at 50% 48%, black 38%, rgba(0,0,0,0.85) 46%, transparent 58%)",
                                }}
                                className="relative z-10 w-full h-full object-contain drop-shadow-[0_30px_60px_rgba(243,108,33,0.45)] animate-ae-float"
                                initial={{ scale: 0.85, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{
                                    duration: 1.1,
                                    ease: [0.22, 1, 0.36, 1],
                                    delay: 0.2,
                                }}
                                data-testid="hero-badge-image"
                            />

                            {/* Floating mini chips */}
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.6 }}
                                className="absolute -left-2 sm:-left-6 top-10 bg-white shadow-ae-soft border border-black/5 rounded-2xl px-3 py-2 flex items-center gap-2"
                                data-testid="hero-chip-ai"
                            >
                                <div className="h-2 w-2 rounded-full bg-ae-orange animate-pulse" />
                                <span className="text-xs font-semibold text-ae-ink">
                                    AI · Running
                                </span>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.8 }}
                                className="absolute -right-2 sm:-right-6 bottom-10 bg-white shadow-ae-soft border border-black/5 rounded-2xl px-3 py-2"
                                data-testid="hero-chip-roi"
                            >
                                <div className="text-[10px] uppercase tracking-widest text-ae-gray-500 font-semibold">
                                    ROI
                                </div>
                                <div className="text-sm font-extrabold text-ae-ink">
                                    +312%
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
